import { Button, Table, Modal } from "@telluscompany/design-system";
import { useSearchParams } from "react-router-dom";
import { PlusCircle } from "lucide-react";
import { DeleteModal } from "@telluscompany/design-system";
import { useLocalization } from "@i18n";
import { useCrudModals } from "@shared/hooks";
import { Gs3TablePaginator } from "@shared/components";
import { EMPTY_PAGINATION_META } from "@shared/constants/pagination.constants";
import type {
  ListComplexesResponse,
  ComplexFilters,
} from "../../api/complexes/complexes.types";
import {
  ComplexFormData,
  mapFormDataToCreateRequest,
  mapFormDataToUpdateRequest,
} from "../../schemas";
import {
  useCreateComplexMutation,
  useUpdateComplexMutation,
  useDeleteComplexMutation,
  useUploadComplexLogoMutation,
} from "../../api/complexes/complexes.mutations";
import { CreateComplexForm } from "./CreateComplexForm";
import { EditComplexForm } from "./EditComplexForm";
import { ComplexesFilter } from "./ComplexesFilter";
import { ComplexesActionMenu } from "./ComplexesActionMenu";
import { PaginationMeta } from "@shared/api";
import { useUpdateTableUserPreferences } from "@shared/hooks";
import { useUserContext } from "@/shared/contexts";

interface ComplexesTabProps {
  tenantId: string;
  complexes: ListComplexesResponse[];
  isLoading?: boolean;
  onFilter: (filters: Partial<ComplexFilters>) => void;
  onClearFilter: () => void;
  initialFilters?: Partial<ComplexFilters>;
  paginatedMeta?: PaginationMeta;
}

export const ComplexesTab = ({
  tenantId,
  complexes,
  onFilter,
  onClearFilter,
  initialFilters,
  paginatedMeta,
}: ComplexesTabProps) => {
  const { translate } = useLocalization();

  const {
    modals,
    selectedItem: selectedComplex,
    openCreate,
    openEdit,
    openDelete,
    closeCreate,
    closeEdit,
    closeDelete,
  } = useCrudModals<ListComplexesResponse>();

  const selectedComplexId = selectedComplex?.id ?? "";
  const createMutation = useCreateComplexMutation(tenantId);
  const updateMutation = useUpdateComplexMutation(tenantId, selectedComplexId);
  const deleteMutation = useDeleteComplexMutation(tenantId);
  const uploadLogoMutation = useUploadComplexLogoMutation();

  const { user } = useUserContext();
  const updateTableUserPreferences = useUpdateTableUserPreferences(user?.userPreferences);

  const handleCreateComplex = async (data: ComplexFormData) => {
    const payload = mapFormDataToCreateRequest(data);
    const createdId = await createMutation.mutateAsync(payload);

    if (data.logo[0] instanceof File && createdId) {
      await uploadLogoMutation.mutateAsync({
        tenantId,
        complexId: createdId,
        logo: data.logo[0] as File,
      });
    }

    closeCreate();
  };

  const handleEditComplex = async (data: ComplexFormData) => {
    const payload = mapFormDataToUpdateRequest(data);
    await updateMutation.mutateAsync(payload);

    if (data.logo[0] instanceof File && selectedComplexId) {
      await uploadLogoMutation.mutateAsync({
        tenantId,
        complexId: selectedComplexId,
        logo: data.logo[0] as File,
      });
    }

    closeEdit();
  };

  const handleDeleteComplex = () => {
    if (selectedComplex?.id) {
      deleteMutation.mutate(selectedComplex.id, {
        onSuccess: () => closeDelete(),
      });
    }
  };

  const createActionMenu = (rowData: { rowData: ListComplexesResponse }) => {
    const complex = rowData.rowData;

    return (
      <ComplexesActionMenu
        complex={complex}
        onEdit={() => openEdit(complex)}
        onDelete={() => openDelete(complex)}
      />
    );
  };

  const tableData = (complexes as unknown as Record<string, unknown>[]) ?? [];

  const tableColumns = [
    {
      key: "fantasyName",
      label: translate("management.complexes.table.column.complex"),
      sortType: "text" as const,
      minWidth: "150px",
    },
    {
      key: "cnpj",
      label: translate("management.complexes.form.label.cnpj"),
      sortType: "text" as const,
      minWidth: "150px",
    },
    {
      key: "responsibleName",
      label: translate("management.complexes.table.column.responsible"),
      sortType: "text" as const,
      minWidth: "150px",
    },
  ];

  return (
    <div className="p-4">
      <Table<ListComplexesResponse>
        userId="COMPLEX1"
        usersPreferencesFromServer={user?.userPreferences}
        updateUsersPreferencesOnServer={updateTableUserPreferences}
        useSearchParamsHook={useSearchParams}
        enableSortByUrl
        TableActionMenu={ user?.role === "owner" ? createActionMenu as React.ComponentType : undefined}
        TableButtons={
          user?.role === "owner" && (
          <Button
            size="small"
            variant="primary"
            onClick={openCreate}
            iconLeft={<PlusCircle />}
          >
            {translate("management.complexes.button.new")}
          </Button>
          )
        }
        TableFilter={() => (
          <ComplexesFilter
            onFilter={onFilter}
            onClear={onClearFilter}
            initialFilters={initialFilters}
          />
        )}
        TablePaginator={
          <Gs3TablePaginator
            meta={paginatedMeta ?? EMPTY_PAGINATION_META}
            useUrlState
            useSearchParamsHook={useSearchParams}
          />
        }
        draggable
        enableTableColumnsSelector
        tableColumns={tableColumns}
        tableData={tableData}
        tableId="complexes-table"
        filterButtonText={translate(
          "management.complexes.table.filterButtonText",
        )}
      />

      {/* Modal de Criação */}
      <Modal
        open={modals.create}
        onClose={closeCreate}
        title={translate("management.complexes.modal.title.create")}
        content={
          <CreateComplexForm
            onSubmit={handleCreateComplex}
            onCancel={closeCreate}
            isSubmitting={createMutation.isPending}
          />
        }
      />

      {/* Modal de Edição */}
      <Modal
        open={modals.edit}
        onClose={closeEdit}
        title={translate("management.complexes.modal.title.edit")}
        content={
          <EditComplexForm
            tenantId={tenantId}
            complexId={selectedComplexId}
            onSubmit={handleEditComplex}
            onCancel={closeEdit}
            isSubmitting={updateMutation.isPending}
          />
        }
      />

      {/* Modal de Exclusão */}
      <DeleteModal
        open={modals.delete}
        onClose={closeDelete}
        onConfirm={handleDeleteComplex}
        isDeleting={deleteMutation.isPending}
        itemDescription={selectedComplex?.fantasyName}
        title={translate("common.deleteModalTitle")}
        message={translate("common.deleteModalItemMessage")}
        alertMessage={translate("common.deleteModalAlertMessage")}
        confirmButtonText={translate("common.delete")}
        cancelButtonText={translate("common.cancel")}
        deletingButtonText={translate("common.deleting")}
      />
    </div>
  );
};

export default ComplexesTab;
